import { useCallback, useEffect, useState } from 'react'
import { ApiError, type Api } from './api.js'
import { useOrgApi } from './orgs.js'

/**
 * The org-aware client. Every page goes through this, so every request
 * carries the active organization - the API refuses org-scoped calls
 * without it, and it refuses them with one it cannot match to a membership.
 */
export function useApi(): Api {
  return useOrgApi()
}

export type AsyncState<T> = {
  data: T | null
  loading: boolean
  error: string | null
  reload: () => void
}

/** Small fetch-on-mount helper. Not worth a data library at this size. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  // One state object rather than three separate setState calls. Starting a
  // request is a single transition - loading on, previous error cleared - and
  // splitting it up meant three synchronous updates at the top of the effect,
  // which is what react-hooks flags as a cascading render.
  const [state, setState] = useState<Omit<AsyncState<T>, 'reload'>>({
    data: null,
    loading: true,
    error: null,
  })
  const [nonce, setNonce] = useState(0)

  const reload = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    // Keep whatever data is already on screen while refetching, so a reload
    // does not blank the page and then repaint it.
    setState((prev) => ({ ...prev, loading: true, error: null }))

    fn()
      .then((d) => { if (!cancelled) setState({ data: d, loading: false, error: null }) })
      .catch((e) => {
        if (cancelled) return
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e instanceof ApiError ? e.message : 'Something went wrong.',
        }))
      })

    // Requests in flight when the component unmounts must not set state.
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  return { ...state, reload }
}
